import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useChangePassword } from "./queries";
import { HttpError } from "@shared/http/client";

const schema = z.object({
  currentPassword: z.string().min(1, "Ingresa tu clave actual"),
  newPassword: z.string().min(4, "Mínimo 4 caracteres").max(200),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "Las contraseñas no coinciden",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof schema>;

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ChangePasswordDialog({ open, onClose }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [status, setStatus] = useState<"idle" | "success">("idle");
  const [serverError, setServerError] = useState<string | null>(null);
  const changeMut = useChangePassword();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
    if (!open) { reset(); setStatus("idle"); setServerError(null); }
  }, [open, reset]);

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await changeMut.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      setStatus("success");
    } catch (e) {
      setServerError(e instanceof HttpError ? e.message : "Error cambiando contraseña.");
    }
  };

  return (
    <dialog ref={dialogRef} onCancel={onClose} onClose={onClose} className="detail-dialog">
      <div className="detail-dialog-head">
        <div>
          <h3 className="detail-dialog-title">Cambiar contraseña</h3>
        </div>
        <button type="button" className="detail-dialog-close" onClick={onClose}>✕</button>
      </div>

      <div className="detail-dialog-body">
        {status === "success" ? (
          <div className="alert-inline alert-inline--success alert-inline--md alert-inline--bold">
            ✅ Contraseña actualizada correctamente.
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="wizard-grid">
              <label className="form-field form-field--full">
                <span>Contraseña actual</span>
                <input type="password" {...register("currentPassword")} className={errors.currentPassword ? "campo-requerido-error" : ""} />
                {errors.currentPassword && <div className="alert-inline alert-inline--danger">{errors.currentPassword.message}</div>}
              </label>

              <label className="form-field form-field--full">
                <span>Nueva contraseña</span>
                <input type="password" {...register("newPassword")} className={errors.newPassword ? "campo-requerido-error" : ""} />
                {errors.newPassword && <div className="alert-inline alert-inline--danger">{errors.newPassword.message}</div>}
              </label>

              <label className="form-field form-field--full">
                <span>Confirmar nueva contraseña</span>
                <input type="password" {...register("confirmPassword")} className={errors.confirmPassword ? "campo-requerido-error" : ""} />
                {errors.confirmPassword && <div className="alert-inline alert-inline--danger">{errors.confirmPassword.message}</div>}
              </label>
            </div>

            {serverError && (
              <div className="alert-inline alert-inline--danger alert-inline--md mt-8">⚠️ {serverError}</div>
            )}
          </form>
        )}
      </div>

      <div className="detail-dialog-actions">
        {status === "success" ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>Cerrar</button>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" onClick={handleSubmit(onSubmit)} disabled={changeMut.isPending}>
              {changeMut.isPending ? "Cambiando…" : "Cambiar contraseña"}
            </button>
          </>
        )}
      </div>
    </dialog>
  );
}
